import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearCampData,
} from '../../../redux/slices/campSlice';
import campService from '../services/campService';
import './CampPayment.css';

const API_BASE_URL = process.env.REACT_APP_YK_API_BASE || 'http://localhost:5000/ykrestapi';
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Component ────────────────────────────────────────────────────────────────

const CampPayment = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const campIdParam = searchParams.get('campId');

  const { user } = useSelector((state) => state.auth);
  const { paymentInfo, selectedCamp, campDates, lastRegInfo } = useSelector((state) => state.camp);

  const campId = paymentInfo?.campId || parseInt(campIdParam) || 0;
  const userId = paymentInfo?.userId || user?.User_Id || user?.user_Id || 0;
  const studentId = paymentInfo?.studentId || 0;
  const totalAmount = paymentInfo?.totalAmount || 0;
  const campName = paymentInfo?.campName || selectedCamp?.camp_Name || selectedCamp?.Camp_Name || '';
  const registrationId = paymentInfo?.registrationId || lastRegInfo?.registrationId || 0;
  const selectedWeekNumbers = paymentInfo?.selectedWeekNumbers || [];
  const extendedWeekNumbers = paymentInfo?.extendedWeekNumbers || [];

  // ── Form state ──────────────────────────────────────────────────────────────
  const [paymentMethod, setPaymentMethod] = useState('credit');
  const [formData, setFormData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: `${user?.User_First_Name || user?.user_First_Name || ''} ${user?.User_Last_Name || user?.user_Last_Name || ''}`.trim(),
    accountHolderName: '',
    routingNumber: '',
    accountNumber: '',
    accountType: 'checking',
    bankName: '',
    zipCode: String(user?.User_Zipcode || user?.user_Zipcode || ''),
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [lookingUpBank, setLookingUpBank] = useState(false);

  // ── Processing state ────────────────────────────────────────────────────────
  const [processing, setProcessing] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // ── Camper Form (merged into success popup) ─────────────────────────────────
  const CAMPER_FORM_URL = 'https://schools.procareconnect.com/form/bf7187a4-b61d-449d-afbe-2e8f52f4eb72';
  const [showCamperSection, setShowCamperSection] = useState(false);
  const [camperCountdown, setCamperCountdown] = useState(10);
  const camperTimerRef = useRef(null);

  const startCamperCountdown = useCallback(() => {
    setCamperCountdown(10);
    setShowCamperSection(true);
    camperTimerRef.current = setInterval(() => {
      setCamperCountdown(prev => {
        if (prev <= 1) {
          clearInterval(camperTimerRef.current);
          window.open(CAMPER_FORM_URL, '_blank', 'noopener,noreferrer');
          setShowCamperSection(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const dismissCamperSection = useCallback(() => {
    clearInterval(camperTimerRef.current);
    setShowCamperSection(false);
  }, []);

  useEffect(() => {
    return () => { if (camperTimerRef.current) clearInterval(camperTimerRef.current); };
  }, []);

  // ── reCAPTCHA ───────────────────────────────────────────────────────────────
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState(null);

  // ── Redirect guard ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!paymentInfo && !lastRegInfo) {
      navigate(`/camp/registration?campId=${campIdParam || ''}`);
    }
  }, []);

  // ── reCAPTCHA loader ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) return;
    const loadScript = () =>
      new Promise((resolve) => {
        const w = window;
        if (w.grecaptcha) return resolve();
        const existing = document.querySelector('script[src*="recaptcha"]');
        if (existing) { existing.addEventListener('load', resolve); return; }
        const s = document.createElement('script');
        s.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
        s.async = true;
        s.defer = true;
        s.onload = resolve;
        document.body.appendChild(s);
      });

    const renderWidget = () => {
      const el = document.getElementById('camppay-recaptcha');
      if (!el || recaptchaWidgetId !== null) return;
      const poll = () => {
        const api = window.grecaptcha;
        if (!api) { setTimeout(poll, 150); return; }
        const doRender = () => {
          if (recaptchaWidgetId !== null) return;
          if (typeof api.render !== 'function') { setTimeout(doRender, 150); return; }
          try {
            const id = api.render(el, {
              sitekey: RECAPTCHA_SITE_KEY,
              callback: (tok) => setRecaptchaToken(tok),
              'expired-callback': () => setRecaptchaToken(''),
              'error-callback': () => setRecaptchaToken(''),
            });
            setRecaptchaWidgetId(id);
          } catch (e) { setTimeout(doRender, 200); }
        };
        typeof api.ready === 'function' ? api.ready(doRender) : setTimeout(doRender, 0);
      };
      poll();
    };

    let active = true;
    loadScript().then(() => { if (active) setTimeout(renderWidget, 0); });
    return () => { active = false; };
  }, [RECAPTCHA_SITE_KEY, recaptchaWidgetId]);

  const resetRecaptcha = () => {
    try {
      if (recaptchaWidgetId !== null && window.grecaptcha) {
        window.grecaptcha.reset(recaptchaWidgetId);
        setRecaptchaToken('');
      }
    } catch (e) {
      console.warn('reCAPTCHA reset error:', e);
    }
  };

  // ── Input handlers ──────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let val = value;

    if (name === 'cardNumber') {
      const d = val.replace(/\D/g, '');
      val = d.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
    }
    if (name === 'expiryDate') {
      const d = val.replace(/\D/g, '');
      val = d.length <= 2 ? d : d.slice(0, 2) + '/' + d.slice(2, 4);
    }
    if (name === 'cvv') val = val.replace(/\D/g, '');
    if (name === 'routingNumber') val = val.replace(/\D/g, '').slice(0, 9);
    if (name === 'accountNumber') val = val.replace(/\D/g, '');
    if (name === 'zipCode') val = val.replace(/[^\d-]/g, '').slice(0, 10);

    setFormData((p) => ({ ...p, [name]: val }));

    if (validationErrors[name]) {
      setValidationErrors((p) => { const n = { ...p }; delete n[name]; return n; });
    }

    if (name === 'routingNumber' && val.length === 9) {
      lookupBankName(val);
    }
  };

  const handleMethodChange = (method) => {
    setPaymentMethod(method);
    setValidationErrors({});
  };

  // ── Bank lookup ─────────────────────────────────────────────────────────────
  const lookupBankName = async (routingNumber) => {
    setLookingUpBank(true);
    try {
      const resp = await fetch(
        `${API_BASE_URL}/CommonChannel/GetBankNameByRoutingNumber?routingNumber=${routingNumber}`,
        { headers: { Accept: 'application/json' } }
      );
      if (resp.ok) {
        const data = await resp.json();
        const name = data.bankName || data.name || data.institutionName || '';
        if (name && name !== 'string') {
          setFormData((p) => ({ ...p, bankName: name.trim() }));
        }
      }
    } catch (e) {
      console.warn('Bank lookup error:', e);
    } finally {
      setLookingUpBank(false);
    }
  };

  // ── Validation ──────────────────────────────────────────────────────────────
  const validateCC = () => {
    const errs = {};
    const digits = formData.cardNumber.replace(/\D/g, '');
    if (!digits) errs.cardNumber = 'Card number is required';
    else if (digits.length < 13 || digits.length > 19) errs.cardNumber = 'Card number must be 13–19 digits';

    if (!formData.expiryDate) {
      errs.expiryDate = 'Expiry date is required';
    } else {
      const m = formData.expiryDate.match(/^(\d{2})\/(\d{2})$/);
      if (!m) { errs.expiryDate = 'Use MM/YY format'; }
      else {
        const month = parseInt(m[1], 10);
        const year = parseInt(m[2], 10);
        const now = new Date();
        const cy = now.getFullYear() % 100;
        const cm = now.getMonth() + 1;
        if (month < 1 || month > 12) errs.expiryDate = 'Invalid month';
        else if (year < cy || (year === cy && month < cm)) errs.expiryDate = 'Card has expired';
      }
    }

    if (!formData.cvv) errs.cvv = 'CVV is required';
    else if (formData.cvv.length < 3) errs.cvv = 'CVV must be 3–4 digits';

    const zipRe = /^\d{5}(-\d{4})?$/;
    if (!formData.zipCode.trim()) errs.zipCode = 'ZIP code is required';
    else if (!zipRe.test(formData.zipCode.trim())) errs.zipCode = 'Enter a valid US ZIP code (e.g. 30096 or 30096-1234)';

    return errs;
  };

  const validateACH = () => {
    const errs = {};
    if (!formData.accountHolderName?.trim()) errs.accountHolderName = 'Account holder name is required';
    if (!formData.routingNumber) errs.routingNumber = 'Routing number is required';
    else if (formData.routingNumber.length !== 9) errs.routingNumber = 'Routing number must be 9 digits';
    if (!formData.accountNumber) errs.accountNumber = 'Account number is required';
    else if (formData.accountNumber.length < 5 || formData.accountNumber.length > 17)
      errs.accountNumber = 'Account number must be 5–17 digits';
    if (!formData.bankName?.trim()) errs.bankName = 'Bank name is required';

    const zipRe = /^\d{5}(-\d{4})?$/;
    if (!formData.zipCode.trim()) errs.zipCode = 'ZIP code is required';
    else if (!zipRe.test(formData.zipCode.trim())) errs.zipCode = 'Enter a valid US ZIP code (e.g. 30096 or 30096-1234)';

    return errs;
  };

  // ── Address / state-country helpers ────────────────────────────────────────
  const fetchBillingCodes = async () => {
    const stateId = user?.State_Id || user?.state_Id || user?.User_State || user?.user_State || '';
    const rawCountry = user?.Country_Id || user?.country_Id || user?.User_Country || user?.user_Country || '1';
    const countryId = parseInt(rawCountry) ? String(parseInt(rawCountry)) : '1';

    let stateCode = user?.StateSortCode || user?.stateSortCode || '';
    let countryCode = user?.CountrySortCode || user?.countrySortCode || '';

    if (!stateCode && stateId && countryId) {
      try {
        const r = await fetch(`${API_BASE_URL}/CommonChannel/GetStatesByCountry?countryId=${countryId}`);
        if (r.ok) {
          const states = await r.json();
          const sid = parseInt(stateId);
          const found = states.find((s) => s.UserStateId === sid || s.userStateId === sid || s.state_Id === sid);
          stateCode = found?.ShortCode || found?.shortCode || found?.sortCode || found?.SortCode || '';
        }
      } catch (e) { console.warn('State lookup error:', e); }
    }

    if (!countryCode && countryId) {
      try {
        const r = await fetch(`${API_BASE_URL}/CommonChannel/GetCountries`);
        if (r.ok) {
          const countries = await r.json();
          const cid = parseInt(countryId);
          const found = countries.find((c) => c.country_Id === cid || c.countryId === cid);
          countryCode = found?.sortCode || found?.SortCode || found?.country_short || '';
        }
      } catch (e) { console.warn('Country lookup error:', e); }
    }

    return { stateCode: stateCode || 'GA', countryCode: countryCode || 'US' };
  };

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (RECAPTCHA_SITE_KEY && !recaptchaToken) {
      setValidationErrors({ recaptcha: 'Please complete the reCAPTCHA verification' });
      document.getElementById('camppay-recaptcha')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const errs = paymentMethod === 'credit' ? validateCC() : validateACH();
    if (Object.keys(errs).length > 0) {
      setValidationErrors(errs);
      const firstField = Object.keys(errs)[0];
      document.getElementsByName(firstField)[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setProcessing(true);

    try {
      const { stateCode, countryCode } = await fetchBillingCodes();

      const userName = `${user?.User_First_Name || user?.user_First_Name || ''} ${user?.User_Last_Name || user?.user_Last_Name || ''}`.trim();
      const userEmail = user?.User_Email || user?.user_Email || user?.email || '';
      const userPhone = user?.User_Phone || user?.User_Cell_Phone_No || user?.user_Phone || '';

      const basePayload = {
        amount: totalAmount,
        campId,
        campName,
        userId,
        userEmail,
        userName,
        userPhone,
        streetAddress1: user?.User_Address_Line1 || user?.user_Address_Line1 || '',
        streetAddress2: user?.User_Address_Line2 || user?.user_Address_Line2 || '',
        city: user?.User_City || user?.user_City || '',
        state: stateCode,
        stateCode,
        zipCode: formData.zipCode.trim(),
        postalCode: formData.zipCode.trim(),
        country: countryCode,
        paymentMethod,
        transactionDate: new Date().toISOString(),
      };

      let paymentPayload;
      if (paymentMethod === 'credit') {
        paymentPayload = {
          ...basePayload,
          paymentType: 'CC',
          cardNumber: formData.cardNumber.replace(/\s/g, ''),
          expiryDate: formData.expiryDate,
          cvv: formData.cvv,
          cardholderName: formData.cardholderName,
        };
      } else {
        paymentPayload = {
          ...basePayload,
          paymentType: 'ACH',
          accountHolderName: formData.accountHolderName,
          routingNumber: formData.routingNumber,
          accountNumber: formData.accountNumber,
          accountType: formData.accountType,
          bankName: formData.bankName || 'Financial Institution',
        };
      }

      const timestamp = new Date().toLocaleString();
      const campDateRange = campDates
        ? `${formatDate(campDates.campStartDate || campDates.CampStartDate)} – ${formatDate(campDates.campEndDate || campDates.CampEndDate)}`
        : '';
      const billingAddress = [
        basePayload.streetAddress1, basePayload.city, basePayload.stateCode, basePayload.zipCode,
      ].filter(Boolean).join(', ');
      const studentName = paymentInfo?.studentName || '';
      const allStudentEntries = paymentInfo?.students || [];
      const buildStudentLine = (entry) => {
        const name = entry.studentName || '';
        const wLabel = (entry.selectedWeeksData || []).map(w => `Week ${w.weekNumber} (${w.dateRange})`).join(', ');
        const extNums = entry.extendedWeekNumbers || [];
        const extLabel = extNums.length > 0
          ? `Extended Stay: ${extNums.map(wn => {
              const wd = (entry.selectedWeeksData || []).find(w => w.weekNumber === wn);
              return wd ? `Week ${wn} (${wd.dateRange})` : `Week ${wn}`;
            }).join(', ')}`
          : '';
        return [name, wLabel, extLabel].filter(Boolean).join(' | ');
      };
      const scheduleHtml = allStudentEntries.length > 1
        ? allStudentEntries.map(buildStudentLine).join('\n')
        : (() => {
            const weeksLabel = paymentInfo?.weeksLabel || '';
            const extendedWeekNumbers = paymentInfo?.extendedWeekNumbers || [];
            const selectedWeeksData = paymentInfo?.selectedWeeksData || [];
            const extendedLabel = extendedWeekNumbers.length > 0
              ? `Extended Stay: ${extendedWeekNumbers.map(wn => {
                  const wd = selectedWeeksData.find(w => w.weekNumber === wn);
                  return wd ? `Week ${wn} (${wd.dateRange})` : `Week ${wn}`;
                }).join(', ')}`
              : '';
            return [studentName, weeksLabel, extendedLabel].filter(Boolean).join(' | ');
          })();

      // ── Step tracking (all 4 steps) ─────────────────────────────────────────
      const stepStatus = {
        'Create Order': { success: false, message: 'Pending…' },
        'Payment':      { success: false, message: 'Pending…' },
        'Confirmation': { success: false, message: 'Pending…' },
        'Email':        { success: false, message: 'Pending…' },
      };

      // ── Step 1: Create pending order ────────────────────────────────────────
      let campOrderId = 0;
      let campPaymentId = 0;
      try {
        const orderResp = await campService.createCampOrder({
          userId,
          campId,
          totalAmount,
          feeAmount: totalAmount,
          discountAmount: 0,
          donationAmount: 0,
          accommodationAmount: 0,
          otherDonation: 'NA',
          paymentMethod: paymentMethod === 'credit' ? 'Credit Card' : 'ACH',
        });
        campOrderId = orderResp?.Camp_Order_ID || orderResp?.camp_Order_ID || orderResp?.camp_Order_Id || 0;
        campPaymentId = orderResp?.Camp_Payment_Id || orderResp?.camp_Payment_Id || 0;
        stepStatus['Create Order'] = { success: true, message: `Order #${campOrderId} created` };
      } catch (orderErr) {
        stepStatus['Create Order'] = {
          success: false,
          message: 'Order record could not be created — payment will still proceed',
          error: orderErr?.message || String(orderErr),
        };
        console.error('CreateCampOrder error (non-blocking):', orderErr);
      }

      // ── Step 2: Process payment via Authorize.Net ───────────────────────────
      let paymentResponse;
      let transId = '';
      try {
        paymentResponse = await campService.processPayment(paymentPayload);
        const isApproved =
          paymentResponse?.createTransactionResponse?.transactionResponse?.responseCode === '1' ||
          paymentResponse?.createTransactionResponse?.messages?.resultCode === 'Ok';

        if (!isApproved) {
          const msg =
            paymentResponse?.meta?.errors?.errorMessages?.[0]?.message ||
            paymentResponse?.message ||
            paymentResponse?.createTransactionResponse?.transactionResponse?.messages?.[0]?.description ||
            'Payment was not approved by gateway';
          throw new Error(msg);
        }
        transId = paymentResponse?.createTransactionResponse?.transactionResponse?.transId || '';
        stepStatus['Payment'] = { success: true, message: `Approved · Trans ID: ${transId}` };
      } catch (payErr) {
        stepStatus['Payment'] = {
          success: false,
          message: payErr.message || 'Payment gateway declined or did not respond',
          error: payErr?.message,
        };
        // Payment failed hard — send admin alert, then show error modal
        campService.sendAdminAlertEmail({
          stepStatus, transactionId: transId, campName, campId, userId,
          userName, userEmail, totalAmount,
          paymentMethod: paymentMethod === 'credit' ? 'Credit Card' : 'ACH',
          timestamp, errorDetails: payErr?.message,
        });
        throw payErr;
      }

      // ── Step 3: Confirm registration in DB ─────────────────────────────────
      try {
        await campService.updatePaymentStatus({
          Camp_Id: campId,
          User_Id: userId,
          Camp_Payment_Id: campPaymentId || registrationId,
          Camp_Order_Id: campOrderId,
          Receipt_Id: transId || '0',
        });
        stepStatus['Confirmation'] = { success: true, message: 'Registration confirmed in database' };
      } catch (confErr) {
        stepStatus['Confirmation'] = {
          success: false,
          message: `Payment charged (Trans: ${transId}) but DB confirmation failed — contact support`,
          error: confErr?.message,
        };
        console.error('UpdatePaymentStatus error (payment was charged):', confErr);
      }

      // ── Step 4: Send confirmation email ────────────────────────────────────
      let receiptUrl = '';
      try {
        const emailResp = await campService.sendCampConfirmationEmail({
          toEmail: userEmail, userName, campName, userPhone,
          registrationId, amount: String(totalAmount),
          paymentType: paymentMethod === 'credit' ? 'Credit Card' : 'ACH',
          transactionId: transId, campDateRange, studentName,
          students: allStudentEntries,
          scheduleHtml: scheduleHtml || studentName || campName,
          billingAddress,
          userFirstName: paymentInfo?.userFirstName || user?.User_First_Name || user?.user_First_Name || '',
          userLastName: paymentInfo?.userLastName || user?.User_Last_Name || user?.user_Last_Name || '',
        });
        receiptUrl =
          (typeof emailResp === 'string' ? emailResp : '') ||
          emailResp?.receiptUrl || emailResp?.ReceiptUrl || emailResp?.attachmentUrl ||
          emailResp?.data?.receiptUrl || emailResp?.data?.attachmentUrl || '';
        stepStatus['Email'] = { success: true, message: `Confirmation sent to ${userEmail}` };
      } catch (emailErr) {
        stepStatus['Email'] = {
          success: false,
          message: 'Confirmation email could not be sent — camp staff will follow up',
          error: emailErr?.message,
        };
        console.warn('Email error (non-fatal):', emailErr);
      }

      // ── Step 5: Store receipt URL in DB so MyOrders can show a clickable link ─
      if (receiptUrl && campOrderId) {
        try {
          await campService.updatePaymentStatus({
            Camp_Id: campId,
            User_Id: userId,
            Camp_Payment_Id: campPaymentId || registrationId,
            Camp_Order_Id: campOrderId,
            Receipt_Id: transId || '0',
            Receipt_Url: receiptUrl,
          });
        } catch { /* non-blocking */ }
      }

      // ── Send admin alert whenever any step failed ───────────────────────────
      const anyFailure = Object.values(stepStatus).some((s) => !s.success);
      campService.sendAdminAlertEmail({
        stepStatus, transactionId: transId, campName, campId, userId,
        userName, userEmail, totalAmount,
        paymentMethod: paymentMethod === 'credit' ? 'Credit Card' : 'ACH',
        timestamp,
        errorDetails: anyFailure
          ? Object.entries(stepStatus).filter(([, v]) => !v.success).map(([k, v]) => `${k}: ${v.error || v.message}`).join('\n')
          : null,
      });

      setPaymentResult({
        transactionId: transId,
        campName, totalAmount,
        paymentMethod: paymentMethod === 'credit' ? 'Credit Card' : 'ACH Bank Account',
        transactionDate: timestamp,
        campDateRange, receiptUrl,
        stepStatus,
        hasPartialFailure: anyFailure,
      });
      setShowSuccessModal(true);
      resetRecaptcha();
      // Show ProCare section for students new to this camp (isFirstTimeForCamp !== false = default to showing)
      const campStudents = paymentInfo?.students || [];
      const needsCamperForm = campStudents.length === 0
        || campStudents.some(s => s.isFirstTimeForCamp !== false);
      if (needsCamperForm) startCamperCountdown();
    } catch (err) {
      console.error('Payment error:', err);
      const msg =
        err.response?.data?.meta?.errors?.errorMessages?.[0]?.message ||
        err.response?.data?.message ||
        err.message ||
        'Payment failed. Please try again.';
      setErrorMessage(msg);
      setShowErrorModal(true);
      resetRecaptcha();
    } finally {
      setProcessing(false);
    }
  };

  // ── Done ────────────────────────────────────────────────────────────────────
  const handleDone = () => {
    dispatch(clearCampData());
    setShowSuccessModal(false);
    navigate('/');
  };

  // ─── RENDER ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-8 px-4">
      {/* Header */}
      <div className="max-w-5xl mx-auto mb-4 lg:mb-6 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl lg:text-3xl font-extrabold text-gray-900">Payment Details</h1>
          <p className="text-gray-500 text-xs lg:text-sm mt-0.5">Complete your camp registration payment</p>
        </div>
        <button onClick={() => navigate(-1)}
          className="flex-shrink-0 flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white font-bold px-4 py-2 lg:px-5 lg:py-2.5 rounded-full shadow-md transition text-sm">
          ← Back
        </button>
      </div>

      <div className="max-w-5xl mx-auto flex flex-col lg:flex-row gap-4 lg:gap-6 items-start">
        {/* ── Order Summary (Enrollment Summary style) ── */}
        <div className="w-full lg:w-2/5 bg-white rounded-2xl shadow-xl p-4 lg:p-6 order-first lg:sticky lg:top-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Enrollment Summary</h2>

            {/* Multi-student or single-student enrollment summary */}
          {(() => {
            const students = paymentInfo?.students;
            const isMulti = students && students.length > 1;

            const renderStudentCard = (s, idx) => {
              const weeksData = s.selectedWeeksData || [];
              const weekFee = s.weekFee || 0;
              const extFee = s.extFee || 0;
              const extWeekNums = s.extendedWeekNumbers || [];
              const subtotal = weeksData.reduce((sum, w) => sum + (w.fee || weekFee), 0);
              const discountAmt = weeksData.length > 1 ? subtotal * 0.10 : 0;
              const extCount = extWeekNums.length;
              const extTotal = weeksData.filter(w => w.hasExtended).reduce((sum, w) => sum + (w.extFee || extFee), 0) || extCount * extFee;
              // Derive display fee from actual week fees (not s.weekFee which is weeks[0].fee)
              const weekFees = weeksData.map(w => w.fee || weekFee);
              const allSameFee = weekFees.length > 0 && weekFees.every(f => f === weekFees[0]);
              const displayFeePerWeek = allSameFee
                ? weekFees[0]
                : (weeksData.length > 0 ? subtotal / weeksData.length : weekFee);
              const gLow = (s.studentGender || '').toLowerCase();
              return (
                <div key={s.studentId || idx} className={`${isMulti ? 'border border-gray-200 rounded-2xl p-4 mb-4' : ''}`}>
                  {/* Student avatar row */}
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 mb-3 flex items-center gap-3 overflow-hidden">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-xl flex-shrink-0">
                      {gLow === 'male' || gLow === 'm' ? '👦' : gLow === 'female' || gLow === 'f' ? '👧' : '👤'}
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-gray-900 text-sm truncate" title={s.studentName}>{s.studentName}</p>
                      <p className="text-gray-500 text-xs">{weeksData.length} week{weeksData.length !== 1 ? 's' : ''} selected</p>
                    </div>
                  </div>
                  {/* Week cards */}
                  {weeksData.length > 0 && (
                    <div className="space-y-1.5 mb-3">
                      {weeksData.map((w) => (
                        <div key={w.weekNumber} className="rounded-lg p-2.5 border bg-white border-gray-200 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-7 h-7 rounded-md bg-gray-700 text-white flex items-center justify-center font-bold text-xs shadow-[0_2px_5px_rgba(0,0,0,0.18)] flex-shrink-0">{w.weekNumber}</div>
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-800 text-xs truncate" title={w.label}>{w.label}</p>
                              <p className="text-gray-400 text-xs truncate">{w.dateRange}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-gray-800 text-xs">${w.fee}</p>
                            {w.hasExtended && <p className="text-orange-500 text-xs">+${extFee} stay</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Per-student subtotal */}
                  <div className="bg-gray-50 rounded-xl p-3 border border-gray-200 shadow-[0_2px_0_rgba(0,0,0,0.06),inset_0_1px_0_rgba(255,255,255,1)] text-xs space-y-1">
                    {weeksData.length > 1 && <div className="flex justify-between"><span className="text-gray-500">{weeksData.length} weeks × ${allSameFee ? displayFeePerWeek : displayFeePerWeek.toFixed(2)}</span><span className="font-semibold text-gray-800">${subtotal.toFixed(2)}</span></div>}
                    {discountAmt > 0 && <div className="flex justify-between"><span className="text-green-600">10% multi-week discount</span><span className="font-semibold text-green-600">−${discountAmt.toFixed(2)}</span></div>}
                    {extCount > 0 && <div className="flex justify-between"><span className="text-orange-500">Extended stay × {extCount}</span><span className="font-semibold text-orange-500">+${extTotal.toFixed(2)}</span></div>}
                    <div className="border-t border-gray-200 pt-1 flex justify-between font-bold"><span>Subtotal</span><span className="text-gray-900">${s.grandTotal?.toFixed(2) || '0.00'}</span></div>
                  </div>
                </div>
              );
            };

            return (
              <>
                {isMulti ? (
                  <>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">{students.length} Students</p>
                    {students.map((s, i) => renderStudentCard(s, i))}
                  </>
                ) : (
                  renderStudentCard(students?.[0] || {
                    studentName: paymentInfo?.studentName,
                    studentGender: paymentInfo?.studentGender,
                    selectedWeeksData: paymentInfo?.selectedWeeksData || [],
                    extendedWeekNumbers: paymentInfo?.extendedWeekNumbers || [],
                    weekFee: paymentInfo?.weekFee || 0,
                    extFee: paymentInfo?.extFee || 0,
                    grandTotal: paymentInfo?.totalAmount || 0,
                  }, 0)
                )}
                {/* Grand total */}
                <div className="border-t-2 border-gray-200 pt-4 mt-2 flex justify-between items-center">
                  <span className="font-black text-gray-900 text-base">Total Amount</span>
                  <span className="font-black text-2xl text-gray-900">${parseFloat(totalAmount).toFixed(2)}</span>
                </div>
              </>
            );
          })()}
        </div>

        {/* ── Payment Form ── */}
        <div className="flex-1 w-full bg-white rounded-2xl shadow-xl p-4 lg:p-6">
          <p className="text-base font-bold text-gray-900 mb-4">Select Payment Method</p>

          {/* Method tabs */}
          <div className="flex rounded-xl overflow-hidden border border-gray-200 mb-6">
            <button type="button"
              className={`flex-1 py-2.5 font-semibold text-sm transition ${
                paymentMethod === 'credit' ? 'bg-gray-800 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.15)]' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => handleMethodChange('credit')}>
              💳 Credit/Debit Card
            </button>
            <button type="button"
              className={`flex-1 py-2.5 font-semibold text-sm transition border-l border-gray-200 ${
                paymentMethod === 'ach' ? 'bg-gray-800 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.15)]' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => handleMethodChange('ach')}>
              🏦 Bank Account (ACH)
            </button>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {paymentMethod === 'credit' ? (
              <>
                <p className="text-sm font-semibold text-gray-700 mb-4">Card Information</p>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Card Number *</label>
                  <input name="cardNumber" value={formData.cardNumber} onChange={handleInputChange}
                    placeholder="1234 5678 9012 3456" maxLength="19" autoComplete="cc-number"
                    className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 outline-none transition ${
                      validationErrors.cardNumber ? 'border-red-400 bg-red-50' : 'border-gray-300'
                    }`} />
                  {validationErrors.cardNumber && <p className="text-red-500 text-xs mt-1">{validationErrors.cardNumber}</p>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date *</label>
                    <input name="expiryDate" value={formData.expiryDate} onChange={handleInputChange}
                      placeholder="MM/YY" maxLength="5" autoComplete="cc-exp"
                      className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-300 outline-none transition ${
                        validationErrors.expiryDate ? 'border-red-400 bg-red-50' : 'border-gray-300'
                      }`} />
                    {validationErrors.expiryDate && <p className="text-red-500 text-xs mt-1">{validationErrors.expiryDate}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CVV *</label>
                    <input name="cvv" type="password" value={formData.cvv} onChange={handleInputChange}
                      placeholder="123" maxLength="4" autoComplete="cc-csc"
                      className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-300 outline-none transition ${
                        validationErrors.cvv ? 'border-red-400 bg-red-50' : 'border-gray-300'
                      }`} />
                    {validationErrors.cvv && <p className="text-red-500 text-xs mt-1">{validationErrors.cvv}</p>}
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cardholder Name *</label>
                  <input name="cardholderName" value={formData.cardholderName} onChange={handleInputChange}
                    placeholder="Name on card" autoComplete="cc-name"
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-300 outline-none transition" />
                  <p className="text-gray-400 text-xs mt-1">✏️ You can edit this if the card is in a different name</p>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Billing ZIP Code *</label>
                  <input name="zipCode" value={formData.zipCode} onChange={handleInputChange}
                    placeholder="e.g. 30096 or 30096-1234" maxLength="10" autoComplete="postal-code"
                    className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-300 outline-none transition ${
                      validationErrors.zipCode ? 'border-red-400 bg-red-50' : 'border-gray-300'
                    }`} />
                  {validationErrors.zipCode && <p className="text-red-500 text-xs mt-1">{validationErrors.zipCode}</p>}
                </div>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold text-gray-700 mb-4">Bank Account Information</p>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Holder Name *</label>
                  <input name="accountHolderName" value={formData.accountHolderName} onChange={handleInputChange}
                    placeholder="Full name as on bank account"
                    className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-300 outline-none transition ${
                      validationErrors.accountHolderName ? 'border-red-400 bg-red-50' : 'border-gray-300'
                    }`} />
                  {validationErrors.accountHolderName && <p className="text-red-500 text-xs mt-1">{validationErrors.accountHolderName}</p>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Routing Number *</label>
                    <div className="relative">
                      <input name="routingNumber" value={formData.routingNumber} onChange={handleInputChange}
                        placeholder="9-digit routing" maxLength="9"
                        className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-300 outline-none transition ${
                          validationErrors.routingNumber ? 'border-red-400 bg-red-50' : 'border-gray-300'
                        }`} />
                      {lookingUpBank && <div className="absolute right-3 top-3 w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />}
                    </div>
                    {validationErrors.routingNumber && <p className="text-red-500 text-xs mt-1">{validationErrors.routingNumber}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Number *</label>
                    <input name="accountNumber" value={formData.accountNumber} onChange={handleInputChange}
                      placeholder="Account number" maxLength="17"
                      className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-300 outline-none transition ${
                        validationErrors.accountNumber ? 'border-red-400 bg-red-50' : 'border-gray-300'
                      }`} />
                    {validationErrors.accountNumber && <p className="text-red-500 text-xs mt-1">{validationErrors.accountNumber}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Type *</label>
                    <select name="accountType" value={formData.accountType} onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-300 outline-none transition">
                      <option value="checking">Checking</option>
                      <option value="savings">Savings</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name *</label>
                    <input name="bankName" value={formData.bankName} onChange={handleInputChange}
                      placeholder="Auto-filled or enter manually"
                      className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-300 outline-none transition ${
                        validationErrors.bankName ? 'border-red-400 bg-red-50' : 'border-gray-300'
                      }`} />
                    {validationErrors.bankName && <p className="text-red-500 text-xs mt-1">{validationErrors.bankName}</p>}
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Billing ZIP Code *</label>
                  <input name="zipCode" value={formData.zipCode} onChange={handleInputChange}
                    placeholder="e.g. 30096 or 30096-1234" maxLength="10" autoComplete="postal-code"
                    className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-300 outline-none transition ${
                      validationErrors.zipCode ? 'border-red-400 bg-red-50' : 'border-gray-300'
                    }`} />
                  {validationErrors.zipCode && <p className="text-red-500 text-xs mt-1">{validationErrors.zipCode}</p>}
                </div>
              </>
            )}

            {/* reCAPTCHA */}
            {RECAPTCHA_SITE_KEY && (
              <div className="my-5">
                <div id="camppay-recaptcha" />
                {validationErrors.recaptcha && <p className="text-red-500 text-xs mt-1">{validationErrors.recaptcha}</p>}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-3 mt-4">
              <button type="button" onClick={() => navigate(-1)}
                className="flex-1 py-3 border border-gray-300 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition">
                Cancel
              </button>
              <button type="submit" disabled={processing}
                className="flex-1 bg-white hover:bg-gray-50 disabled:opacity-40 text-gray-800 font-bold py-3 rounded-xl border-2 border-gray-300 shadow-[0_4px_0_rgba(0,0,0,0.1),0_4px_12px_rgba(0,0,0,0.06)] hover:shadow-[0_2px_0_rgba(0,0,0,0.1)] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] transition-all flex items-center justify-center gap-2">
                {processing ? (
                  <><div className="w-4 h-4 border-2 border-gray-700 border-t-transparent rounded-full animate-spin" /> Processing…</>
                ) : (
                  `Pay $${parseFloat(totalAmount).toFixed(2)}`
                )}
              </button>
            </div>
            <p className="text-center text-gray-400 text-xs mt-3">🔒 Secured by Authorize.Net · 256-bit SSL encryption</p>
          </form>
        </div>
      </div>

      {/* ── Payment Processing Overlay ── */}
      {processing && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-md z-50 flex items-center justify-center">
          <div className="text-center select-none">
            {/* Ripple rings */}
            <div className="relative inline-flex items-center justify-center mb-8">
              <span className="absolute inline-flex w-40 h-40 rounded-full bg-white/10 animate-ping" style={{ animationDuration: '1.4s' }} />
              <span className="absolute inline-flex w-32 h-32 rounded-full bg-white/15 animate-ping" style={{ animationDuration: '1.1s', animationDelay: '0.2s' }} />
              {/* Logo circle */}
              <div className="relative w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-[0_0_40px_rgba(255,255,255,0.25)] z-10">
                <img
                  src="https://www.radhamadhavsociety.org/assets/images/about/i-logo-2.png"
                  alt="RMS"
                  className="w-16 h-16 object-contain"
                  style={{ animation: 'rmsLogoPulse 2s ease-in-out infinite' }}
                />
              </div>
            </div>
            <h3 className="text-white text-2xl font-bold mb-1 tracking-tight">Processing Payment</h3>
            <p className="text-white/60 text-sm mb-6">Please wait · do not close this window</p>
            <div className="flex justify-center gap-2">
              {[0, 150, 300].map((delay) => (
                <div key={delay} className="w-2.5 h-2.5 bg-white/80 rounded-full animate-bounce" style={{ animationDelay: `${delay}ms` }} />
              ))}
            </div>
          </div>
          <style>{`
            @keyframes rmsLogoPulse {
              0%, 100% { transform: scale(1); opacity: 1; }
              50% { transform: scale(1.08); opacity: 0.85; }
            }
          `}</style>
        </div>
      )}

      {/* ── Success / Partial-Success Modal ── */}
      {showSuccessModal && (() => {
        const partial = paymentResult?.hasPartialFailure;
        const headerBg = partial
          ? 'bg-gradient-to-r from-amber-500 to-orange-500'
          : 'bg-gradient-to-r from-green-500 to-emerald-600';
        return (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden flex flex-col" style={{maxHeight:'min(90dvh,90vh)'}}>
              {/* Header */}
              <div className={`${headerBg} p-6 text-center flex-shrink-0`}>
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                  <span className="text-2xl">{partial ? '⚠️' : '✅'}</span>
                </div>
                <h2 className="text-xl font-extrabold text-white">
                  {partial ? 'Payment Processed — Action May Be Needed' : 'Payment Successful!'}
                </h2>
                <p className="text-white/80 text-sm mt-1">
                  {partial
                    ? 'Your card was charged. Some steps had issues — camp staff has been alerted.'
                    : 'Your camp registration is confirmed'}
                </p>
              </div>

              {/* ── ProCare notice — always visible, never scrolls away ── */}
              {showCamperSection && (
                <div className="flex-shrink-0 border-b border-indigo-100 bg-gradient-to-b from-indigo-50 to-white px-4 py-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-indigo-800 font-bold text-sm">📋 One Last Step — Camper Form</p>
                    <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-extrabold text-xs">{camperCountdown}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-2.5 mb-2">
                    <span className="text-lg flex-shrink-0">⚠️</span>
                    <div>
                      <p className="font-bold text-amber-800 text-xs">State Compliance Notice</p>
                      <p className="text-amber-700 text-xs mt-0.5">Georgia state law requires completed camper health files before your child can attend camp.</p>
                    </div>
                  </div>
                  <div className="h-1 bg-gray-100 rounded-full overflow-hidden mb-2">
                    <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full transition-all duration-1000"
                      style={{ width: `${(camperCountdown / 10) * 100}%` }} />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={dismissCamperSection}
                      className="flex-1 border-2 border-gray-200 rounded-xl text-gray-600 font-semibold hover:bg-gray-50 transition text-xs" style={{minHeight:34}}>
                      Skip for Now
                    </button>
                    <a href={CAMPER_FORM_URL} target="_blank" rel="noopener noreferrer"
                      onClick={dismissCamperSection}
                      className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-700 text-white font-bold rounded-xl transition text-xs flex items-center justify-center gap-1 hover:opacity-90" style={{minHeight:34}}>
                      Complete Form 📋
                    </a>
                  </div>
                </div>
              )}

              {/* Scrollable body */}
              <div className="overflow-y-auto flex-1 p-5 space-y-4">
                {/* Key details */}
                <div className="bg-gray-50 rounded-2xl p-4 space-y-2.5">
                  {paymentResult?.transactionId && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Transaction ID</span>
                      <span className="font-mono font-bold text-gray-800 bg-gray-200 px-2 py-0.5 rounded">{paymentResult.transactionId}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Camp</span>
                    <span className="font-semibold text-gray-800 text-right max-w-[55%]">{paymentResult?.campName}</span>
                  </div>
                  {paymentResult?.campDateRange && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Dates</span>
                      <span className="font-semibold text-gray-800">{paymentResult.campDateRange}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Amount Charged</span>
                    <span className="font-bold text-green-600 text-base">${parseFloat(paymentResult?.totalAmount || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Method</span>
                    <span className="font-semibold text-gray-800">{paymentResult?.paymentMethod}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Date & Time</span>
                    <span className="font-semibold text-gray-800">{paymentResult?.transactionDate}</span>
                  </div>
                </div>

                {/* Step-by-step status */}
                {paymentResult?.stepStatus && (
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Processing Steps</p>
                    <div className="space-y-2">
                      {Object.entries(paymentResult.stepStatus).map(([key, val]) => (
                        <div key={key} className={`rounded-xl px-3 py-2.5 flex items-start gap-3 ${val.success ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'}`}>
                          <span className="text-base flex-shrink-0 mt-0.5">{val.success ? '✅' : '❌'}</span>
                          <div className="min-w-0">
                            <p className={`text-xs font-bold ${val.success ? 'text-green-700' : 'text-red-700'}`}>{key}</p>
                            <p className={`text-xs mt-0.5 ${val.success ? 'text-green-600' : 'text-red-600'}`}>{val.message}</p>
                            {val.error && !val.success && (
                              <p className="text-xs text-red-400 mt-0.5 font-mono break-all">{val.error}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Partial failure notice */}
                {partial && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
                    <p className="font-bold mb-1">📧 Camp staff has been notified automatically.</p>
                    <p>Please save your <strong>Transaction ID</strong> above. If you don't receive a confirmation email within 24 hours, contact us at <strong>campregistration@yugalkunj.org</strong>.</p>
                  </div>
                )}

                {/* Receipt buttons */}
                {paymentResult?.receiptUrl && (
                  <div className="flex gap-3">
                    <a href={paymentResult.receiptUrl} target="_blank" rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-1.5 bg-indigo-600 text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-indigo-700 transition">
                      👁 View Receipt
                    </a>
                    <a href={paymentResult.receiptUrl} download
                      className="flex-1 flex items-center justify-center gap-1.5 bg-emerald-600 text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-emerald-700 transition">
                      ⬇ Download
                    </a>
                  </div>
                )}

              </div>

              {/* Footer */}
              <div className="px-5 flex-shrink-0" style={{paddingBottom:'max(20px,env(safe-area-inset-bottom,20px))'}}>
                <button onClick={handleDone}
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white font-bold rounded-xl transition text-sm" style={{minHeight:48}}>
                  Done
                </button>
              </div>
            </div>
          </div>
        );
      })()}


      {/* ── Error Modal ── */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden">
            <div className="bg-gradient-to-r from-red-500 to-rose-600 p-6 text-center">
              <div className="text-4xl mb-2">⚠️</div>
              <h2 className="text-xl font-extrabold text-white">Payment Failed</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-700 text-sm text-center">{errorMessage}</p>
            </div>
            <div className="px-6 flex gap-3" style={{paddingBottom:'max(24px,env(safe-area-inset-bottom,24px))'}}>
              <button onClick={() => { setShowErrorModal(false); navigate(-1); }}
                className="flex-1 border border-gray-300 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition text-sm" style={{minHeight:48}}>
                Cancel
              </button>
              <button onClick={() => setShowErrorModal(false)}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition text-sm" style={{minHeight:48}}>
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampPayment;
